import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * CozyOnCozy Halloween deal (buy box). Replaces the page's picker and Add to cart while it shows:
 * "Buy 2" (each item with its own options + a matching pair of free Cloud Socks) or "Buy 1".
 * The choices live in window state, so they survive the theme re-rendering the block on a variant change.
 * Picking a colour jumps the product photos to that colour's picture.
 */
(() => {
  if (window.__cozyHwdBound) return;
  window.__cozyHwdBound = true;

  /** @type {{mode: number, items: string[][] } | null} */
  let state = null;

  const data = (card) => {
    if (!card.__hwd) {
      try {
        card.__hwd = JSON.parse(card.querySelector('[data-cozy-hwd-data]')?.textContent || '{}');
      } catch {
        card.__hwd = { variants: [] };
      }
    }
    return card.__hwd;
  };

  const money = (cents, format) => {
    const n = (cents / 100).toFixed(2);
    return (format || '${{amount}}')
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(cents / 100)))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, n.replace('.', ','))
      .replace(/\{\{\s*amount\s*\}\}/, n.replace(/\B(?=(\d{3})+(?!\d))/g, ','));
  };

  const find = (card, opts) => data(card).variants.find((v) => v.opts.every((o, i) => o === opts[i]));

  // Picking a value that doesn't exist with the other choices moves to the nearest available variant that has it
  const choose = (card, opts, oi, value) => {
    const next = opts.slice();
    next[oi] = value;
    const exact = find(card, next);
    if (exact && exact.ok) return next;
    const alt = data(card).variants
      .filter((v) => v.ok && v.opts[oi] === value)
      .sort((a, b) => b.opts.filter((o, i) => o === opts[i]).length - a.opts.filter((o, i) => o === opts[i]).length)[0];
    return alt ? alt.opts.slice() : next;
  };

  const initState = (card) => {
    if (state) return;
    const { variants, start } = data(card);
    const first = variants.find((v) => v.id === start && v.ok) || variants.find((v) => v.ok) || variants[0];
    state = { mode: 2, items: [first.opts.slice(), first.opts.slice()] };
  };

  const render = (card) => {
    initState(card);
    const { money: format } = data(card);
    card.querySelectorAll('[data-cozy-hwd-mode]').forEach((choice) => {
      const on = Number(choice.dataset.cozyHwdMode) === state.mode;
      choice.classList.toggle('is-on', on);
      choice.setAttribute('aria-checked', String(on));
    });
    card.querySelectorAll('[data-cozy-hwd-item]').forEach((row) => {
      const opts = state.items[Number(row.dataset.cozyHwdItem)];
      row.querySelectorAll('select[data-cozy-hwd-sel]').forEach((select) => {
        const oi = Number(select.dataset.cozyHwdSel);
        Array.from(select.options).forEach((option) => {
          const anyOk = data(card).variants.some((x) => x.ok && x.opts[oi] === option.value);
          option.dataset.label = option.dataset.label || option.textContent;
          // A value sold out only with the other choices can still be picked (choose() moves them); fully sold out can't
          option.disabled = !anyOk;
          option.textContent = option.dataset.label + (anyOk ? '' : ' (sold out)');
        });
        select.value = opts[oi];
        const dot = select.parentElement?.querySelector('[data-cozy-hwd-dot]');
        if (dot instanceof HTMLElement) dot.style.background = select.selectedOptions[0]?.dataset.color || '#ddd';
      });
    });

    // Display only: the Halloween deal discount (Shopify Function on the Aipilot app) takes 10% off each winter item
    // and makes 2 pairs of socks free
    const v = state.items.map((opts) => find(card, opts));
    // Shopify rounds the 10% down to the cent (44.97 → 4.49 off), so do the same
    const net = (x) => x.price - Math.floor(x.price * 0.1);
    const set = (sel, text) => card.querySelectorAll(sel).forEach((el) => (el.textContent = text));
    // Buy 1 is the regular (already reduced) price: the extra 10% only starts at 2 winter items
    if (v[0]) {
      set('[data-cozy-hwd-now="1"]', money(v[0].price, format));
      const reduced = v[0].compare > v[0].price;
      set('[data-cozy-hwd-was="1"]', reduced ? money(v[0].compare, format) : '');
      set('[data-cozy-hwd-off]', reduced ? `${Math.round((1 - v[0].price / v[0].compare) * 100)}%` : '');
    }
    card.querySelectorAll('[data-cozy-hwd-note]').forEach((el) => (el.hidden = state.mode !== 2));
    if (v[0] && v[1]) {
      set('[data-cozy-hwd-now="2"]', money(net(v[0]) + net(v[1]), format));
      const was = v[0].price + v[1].price + v[0].sockPrice + v[1].sockPrice;
      set('[data-cozy-hwd-was="2"]', money(was, format));
      set('[data-cozy-hwd-save]', money(was - net(v[0]) - net(v[1]), format));
    }
    // The free socks: picture, colour and what they'd cost, for each item
    card.querySelectorAll('[data-cozy-hwd-sock]').forEach((el) => {
      const x = v[Number(el.dataset.cozyHwdSock)];
      if (!x) return;
      const img = el.querySelector('img');
      if (img && x.sockImg && img.getAttribute('src') !== x.sockImg) img.src = x.sockImg;
      const name = el.querySelector('[data-cozy-hwd-sock-name]');
      if (name) name.textContent = x.sockName;
      const was = el.querySelector('[data-cozy-hwd-sock-was]');
      if (was) was.textContent = money(x.sockPrice, format);
    });
    const chosen = state.mode === 2 ? v : [v[0]];
    const ok = chosen.every((x) => x && x.ok);
    const btn = card.querySelector('[data-cozy-hwd-add]');
    const label = btn?.querySelector('.cozy-hwd__btn-label');
    if (btn && !card.classList.contains('is-loading')) {
      btn.toggleAttribute('disabled', !ok);
      if (label && label.textContent !== 'Added') label.textContent = ok ? 'Add to cart' : 'Sold out';
    }
  };

  // Show the picked colour in the product photos: jump the page's gallery to that variant's picture.
  // Thickness/size picks don't move the photos (owner, 25 Oct).
  const showColour = (card, i) => {
    const v = find(card, state.items[i]);
    if (!v || !v.img) return;
    const scope = card.closest('.shopify-section') || document;
    scope.querySelectorAll('media-gallery slideshow-component').forEach((gallery) => {
      const slides = Array.from(gallery.querySelectorAll(':scope slideshow-slide'));
      const index = slides.findIndex((slide) => {
        const img = slide.querySelector('img');
        const src = img?.getAttribute('src') || img?.currentSrc || '';
        return src.split('?')[0].split('/').pop() === v.img;
      });
      if (index >= 0 && typeof gallery.select === 'function') gallery.select(index);
    });
  };

  const all = () => document.querySelectorAll('[data-cozy-hwd]');
  const init = () => all().forEach(render);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  // The theme re-renders the block after a variant change; put the shopper's choices back
  document.addEventListener('variant:update', () => setTimeout(init, 0));

  const selectMode = (choice) => {
    const card = choice.closest('[data-cozy-hwd]');
    if (!(card instanceof HTMLElement)) return;
    state.mode = Number(choice.dataset.cozyHwdMode);
    card.classList.remove('is-added');
    const label = card.querySelector('.cozy-hwd__btn-label');
    if (label) label.textContent = 'Add to cart';
    render(card);
  };

  document.addEventListener('change', (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.hasAttribute('data-cozy-hwd-sel')) return;
    const card = select.closest('[data-cozy-hwd]');
    const row = select.closest('[data-cozy-hwd-item]');
    if (!(card instanceof HTMLElement) || !(row instanceof HTMLElement)) return;
    const i = Number(row.dataset.cozyHwdItem);
    const colIdx = data(card).colIdx;
    const before = state.items[i][colIdx];
    state.items[i] = choose(card, state.items[i], Number(select.dataset.cozyHwdSel), select.value);
    card.classList.remove('is-added');
    const label = card.querySelector('.cozy-hwd__btn-label');
    if (label) label.textContent = 'Add to cart';
    render(card);
    if (colIdx >= 0 && state.items[i][colIdx] !== before) showColour(card, i);
  });

  document.addEventListener('keydown', (event) => {
    const choice = event.target instanceof Element ? event.target.closest('[data-cozy-hwd-mode]') : null;
    if (choice && (event.key === 'Enter' || event.key === ' ') && event.target === choice) {
      event.preventDefault();
      selectMode(choice);
    }
  });

  document.addEventListener('click', async (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const card = target?.closest('[data-cozy-hwd]');
    if (!(card instanceof HTMLElement)) return;

    if (target.closest('select')) return;

    const choice = target.closest('[data-cozy-hwd-mode]');
    if (choice instanceof HTMLElement && !choice.classList.contains('is-on')) {
      selectMode(choice);
      return;
    }

    const btn = target.closest('[data-cozy-hwd-add]');
    if (!btn) return;
    const chosen = (state.mode === 2 ? state.items : [state.items[0]]).map((opts) => find(card, opts));
    if (!chosen.every((v) => v && v.ok)) return;

    const qty = new Map();
    const add = (id, n) => id && qty.set(id, (qty.get(id) || 0) + n);
    chosen.forEach((v) => {
      add(v.id, 1);
      if (state.mode === 2) add(v.sock, 1);
    });
    // One free gift box per item. In the 2-pack the 2 boxes are also what triggers the free-socks discount
    // (a trigger on the winter items themselves would block the extra 10%), so they always come along.
    add(data(card).gift, chosen.length);

    const sections = new Set();
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) sections.add(el.dataset.sectionId);
    });

    card.classList.add('is-loading');
    btn.setAttribute('disabled', '');
    const label = btn.querySelector('.cozy-hwd__btn-label');
    try {
      const body = JSON.stringify({
        items: Array.from(qty, ([id, quantity]) => ({ id, quantity })),
        sections: Array.from(sections).join(','),
        sections_url: window.location.pathname,
      });
      const res = await fetch(`${Theme.routes.cart_add_url}`, fetchConfig('json', { body }));
      const added = await res.json();
      if (!res.ok || added.status) throw new Error(added.description || added.message || 'Could not add to cart');

      card.classList.remove('is-loading');
      btn.removeAttribute('disabled');
      if (label) label.textContent = 'Added';

      const cart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      document.dispatchEvent(
        new CartAddEvent(cart, 'cozy-halloween-deal', {
          source: 'cozy-halloween-deal',
          itemCount: cart.item_count,
          variantId: String(chosen[0].id),
          sections: added.sections,
        })
      );
    } catch (err) {
      console.error('[cozy-halloween-deal]', err);
      card.classList.remove('is-loading');
      btn.removeAttribute('disabled');
      if (label) label.textContent = 'Retry';
    }
  });
})();
